/**
 * TypeScript types for 3D Warehouse Layout visualization
 */

// Import from existing rack-builder types
import type { RackSummary } from '../../rack-builder/types';
export type { RackSummary };

// ============================================
// Core Layout Types
// ============================================

export interface WarehouseLayout {
  warehouseId: string;
  name: string;
  dimensions: WarehouseDimensions;
  gridUnit: number; // Snap-to-grid in meters (e.g., 0.5)
  racks: RackPosition[];
  objects: WarehouseObject[];
  version: number;
  lastModified: string;
}

export interface WarehouseDimensions {
  length: number; // X-axis in meters
  width: number;  // Y-axis in meters
  height: number; // Z-axis in meters
}

export interface RackPosition {
  rackNumber: string;
  x: number;           // Position in meters from origin
  y: number;           // Position in meters from origin
  rotation: 0 | 90 | 180 | 270;
  label?: string;      // Optional display name
  color?: string;      // Optional custom color
}

export interface WarehouseObject {
  id: string;
  type: WarehouseObjectType;
  name: string;
  x: number;
  y: number;
  width: number;
  depth: number;
  height?: number;
  rotation?: number;
  color?: string;
}

export type WarehouseObjectType =
  | 'pillar'       // Søyle
  | 'door'         // Port/dør
  | 'station'      // Pakkestasjon
  | 'charging'     // Ladestasjon
  | 'zone_marker'; // Sonemarkør

// ============================================
// Rack Structure Types (from existing rack-builder)
// ============================================

export interface RackDimensions {
  width: number;      // Total width in meters
  depth: number;      // Total depth in meters
  height: number;     // Total height in meters
  verticals: number;  // Number of vertical positions
  shelves: number;    // Number of shelf levels
  drawers?: number;   // Optional drawers per cell
}

// ============================================
// Activity Types (for Phase 2 integration)
// ============================================

export interface LocationActivity {
  binCode: string;
  rackNumber: string;
  vertical: number;
  shelf: number;
  activityType: ActivityType;
  timestamp: Date;
  operatorName?: string;
  itemNo?: string;
  quantity?: number;
}

export type ActivityType =
  | 'Pick'
  | 'PutAway'
  | 'Transfer'
  | 'Correction'
  | 'Counting'
  | 'Receiving'
  | 'Reservation';

export interface HeatmapData {
  binCode: string;
  rackNumber: string;
  vertical: number;
  shelf: number;
  intensity: number; // 0-1 normalized value
  activityCount: number;
}

// ============================================
// UI State Types
// ============================================

export type ViewMode = '2d' | '3d' | 'first-person';

export type EditorTool =
  | 'select'   // Select and move items
  | 'rack'     // Place racks
  | 'object'   // Place objects
  | 'measure'  // Measure distances
  | 'delete';  // Delete items

export type PlacementMode = 'single' | 'linear' | 'mirror' | 'grid';
export type PlacementDirection = 'horizontal' | 'vertical';

export interface PlacementConfig {
  mode: PlacementMode;
  direction: PlacementDirection;
  aisleWidth: number;        // Gang mellom rader (meter)
  rackSpacing: number;       // Avstand mellom reoler i rad (meter)
  backToBackSpacing: number; // Rygg-mot-rygg avstand for mirror (meter)
  rows: number;              // Antall rader (for grid)
  columns: number;           // Antall kolonner (for grid)
}

export interface RubberBandState {
  isActive: boolean;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

export interface EditorState {
  viewMode: ViewMode;
  activeTool: EditorTool;
  selectedRacks: Set<string>;      // Multi-select støtte
  selectedObject: string | null;
  anchorRack: string | null;       // Ankerreol for bulk-plassering
  showGrid: boolean;
  showLabels: boolean;
  // Heatmap styres ikke lenger av editorState — se insightMode i useLayoutReducer
  showActivity: boolean;
  zoom: number;
  panOffset: { x: number; y: number };
  rubberBand: RubberBandState;
  placementConfig: PlacementConfig;
  showPlacementPreview: boolean;   // Vis ghost-preview før plassering
}

export interface CameraState {
  position: [number, number, number];
  target: [number, number, number];
  fov: number;
}

// Preview position for ghost racks during bulk placement
export interface PreviewRackPosition {
  rackNumber: string;
  x: number;
  y: number;
  rotation: 0 | 90 | 180 | 270;
  isValid: boolean;  // true hvis posisjonen er gyldig (ingen kollisjon)
}

// ============================================
// Component Props Types
// ============================================

export interface WarehouseLayoutEditorProps {
  warehouseId: string;
  onBack?: () => void;
}

export interface Layout2DCanvasProps {
  layout: WarehouseLayout;
  editorState: EditorState;
  rackSummaries: RackSummary[];
  onLayoutChange: (layout: WarehouseLayout) => void;
  onEditorStateChange: (state: EditorState) => void;
  onSelectionChange: (selectedRacks: Set<string>) => void;
  previewPositions?: PreviewRackPosition[];  // Ghost-reoler for preview
  heatmapData?: HeatmapData[];
  activities?: LocationActivity[];
}

export interface Layout3DSceneProps {
  layout: WarehouseLayout;
  rackSummaries: RackSummary[];
  rackDimensions: Map<string, RackDimensions>;
  editorState: EditorState;
  onRackSelect: (rackNumber: string | null) => void;
  heatmapData?: HeatmapData[];
  activities?: LocationActivity[];
}

export interface Rack3DProps {
  position: RackPosition;
  dimensions: RackDimensions;
  isSelected: boolean;
  onClick: () => void;
  heatmapData?: HeatmapData[];
  activities?: LocationActivity[];
  showHeatmap: boolean;
  showActivity: boolean;
}

// ============================================
// Storage Types
// ============================================

export interface LayoutStorageData {
  layouts: Record<string, WarehouseLayout>;
  lastAccessed: string;
  version: number;
}

// ============================================
// Activity Colors (matching rack-builder-live)
// ============================================

export const ACTIVITY_COLORS: Record<ActivityType, string> = {
  Pick: '#22c55e',       // Green
  PutAway: '#3b82f6',    // Blue
  Transfer: '#f59e0b',   // Amber
  Correction: '#ef4444', // Red
  Counting: '#8b5cf6',   // Purple
  Receiving: '#06b6d4',  // Cyan
  Reservation: '#ec4899' // Pink
};

// ============================================
// Default Values
// ============================================

export const DEFAULT_WAREHOUSE_DIMENSIONS: WarehouseDimensions = {
  length: 30,
  width: 20,
  height: 8
};

export const DEFAULT_GRID_UNIT = 0.5; // meters

export const DEFAULT_RACK_DIMENSIONS: RackDimensions = {
  width: 6.8,
  depth: 1.3,
  height: 2.7,
  verticals: 10,
  shelves: 5
};

export const DEFAULT_PLACEMENT_CONFIG: PlacementConfig = {
  mode: 'single',
  direction: 'horizontal',
  aisleWidth: 2.3,        // Standard plukkgang
  rackSpacing: 0.5,       // Mellomrom i rad
  backToBackSpacing: 0.2, // Rygg-mot-rygg
  rows: 2,
  columns: 5
};

// ============================================
// Smart Layout Types
// ============================================

export type LayoutTemplateName =
  | 'back-to-back'     // Rygg-mot-rygg rader (mest plasseffektiv)
  | 'parallel-rows'    // Enkle parallelle rader
  | 'u-shape'          // Reoler langs 3 vegger
  | 'l-shape'          // Reoler langs 2 vegger
  | 'perimeter';       // Reoler langs alle 4 vegger

export interface SmartLayoutConfig {
  template: LayoutTemplateName;
  orientation: PlacementDirection;
  aisleWidth: number;         // Gang mellom rader (1.5-4.0m)
  rackSpacing: number;        // Avstand mellom reoler i rad (0.2-2.0m)
  backToBack: boolean;        // Rygg-mot-rygg rader
  backToBackGap: number;      // Gap mellom rygg-mot-rygg par (0.1-0.5m)
  wallMargin: number;         // Avstand fra vegg (0.5-3.0m)
}

export interface LayoutMetrics {
  totalRacks: number;
  placedRacks: number;
  failedRacks: number;
  spaceUtilization: number;   // 0-1 (reol-areal / brukbart areal)
  aisleCount: number;
  rowCount: number;
}

export interface LayoutTemplate {
  name: LayoutTemplateName;
  label: string;              // Norsk visningsnavn
  description: string;        // Kort beskrivelse
  icon: string;               // Lucide ikon-navn
  defaultConfig: Partial<SmartLayoutConfig>;
}

export const DEFAULT_SMART_LAYOUT_CONFIG: SmartLayoutConfig = {
  template: 'back-to-back',
  orientation: 'horizontal',
  aisleWidth: 2.3,
  rackSpacing: 0.5,
  backToBack: true,
  backToBackGap: 0.2,
  wallMargin: 1.5,
};

export const DEFAULT_RUBBER_BAND: RubberBandState = {
  isActive: false,
  startX: 0,
  startY: 0,
  currentX: 0,
  currentY: 0
};

export const DEFAULT_EDITOR_STATE: EditorState = {
  viewMode: '2d',
  activeTool: 'select',
  selectedRacks: new Set<string>(),
  selectedObject: null,
  anchorRack: null,
  showGrid: true,
  showLabels: true,
  showActivity: true,
  zoom: 1,
  panOffset: { x: 0, y: 0 },
  rubberBand: DEFAULT_RUBBER_BAND,
  placementConfig: DEFAULT_PLACEMENT_CONFIG,
  showPlacementPreview: false
};
